import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/privacy.css";
import Footerlanding from "../landing/Footerlanding";
import Navbarlanding from "../landing/Navbarlanding";
import { parse } from "tldts";

const PrivacyPolicy = () => {
  const [company, setCompany] = useState({
    name: "Your Company",
    logo: "",
    favicon: "",
    domain: "",
    subdomain: "",
    email: "",
  });
  const [fetchError, setFetchError] = useState("");

  const getDomainInfo = () => {
    const hostname = window.location.hostname;
    const parsed = parse(hostname);
    return {
      domain: parsed.domainWithoutSuffix || "",
      subdomain: parsed.subdomain || "",
    };
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const { domain } = getDomainInfo();
        const response = await axios.get("https://payyug.in/api/get-company", {
          params: { domain },
        });
        const data = response.data;

        if (data && Object.keys(data).length > 0) {
          setCompany((prev) => ({
            ...prev,
            name: data.name || prev.name,
            logo: data.logo || prev.logo,
            favicon: data.favicon || prev.favicon,
            domain: data.domain || prev.domain,
            subdomain: data.subdomain || prev.subdomain,
            email: data.email || prev.email,
          }));

          if (data.favicon) {
            const link =
              document.querySelector("link[rel*='icon']") ||
              document.createElement("link");
            link.type = "image/png";
            link.rel = "icon";
            link.href = `https://payyug.in/${
              data.favicon.startsWith("images/")
                ? data.favicon.slice(7)
                : data.favicon
            }`;
            document.head.appendChild(link);
          }
        } else {
          setFetchError("Company info not found.");
        }
      } catch (err) {
        console.error("Company fetch failed:", err);
        setFetchError("Failed to fetch company info.");
      }
    };

    fetchCompany();
  }, []);

  const supportEmail = company?.email || "support@yourdomain.com";
  const contactEmail = company?.email || "contact@yourdomain.com";

  return (
    <div className="privacy-page">
      <Navbarlanding />

      <div className="privacy-wrapper">
        <h1>Privacy Policy – {company.name}</h1>
        <p className="last-updated">Effective Date: January 2026</p>

        {fetchError && <p className="error-text">{fetchError}</p>}

        <section>
          <p>
            {company.name} (“we”, “our”, “us”) operates the website{" "}
            {company.domain || "yourdomain.com"} and is committed to protecting
            the privacy and personal information of its borrowers and platform users.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, store, protect, and
            disclose information when you access or use our micro-finance & loan management software platform.
          </p>
          <p>
            By using {company.domain || "yourdomain.com"}, you agree to the
            practices described in this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>1. Information We Collect</h2>
          <p>We may collect and process the following types of information:</p>

          <h3>a. Personal Information</h3>
          <ul>
            <li>Full name</li>
            <li>Mobile number</li>
            <li>Email address</li>
            <li>Residential and business address</li>
            <li>Date of birth & employment details</li>
          </ul>

          <h3>b. Identity & KYC Information</h3>
          <ul>
            <li>Government-issued identification details (Aadhaar, PAN, Voter ID, etc.)</li>
            <li>Photographs and documents submitted for credit assessment & verification</li>
          </ul>

          <h3>c. Financial & Loan Information</h3>
          <ul>
            <li>Bank account details for loan disbursal and automated EMI deductions</li>
            <li>Loan application records (requested amount, tenor, collateral details)</li>
            <li>Credit scoring data and repayment history</li>
          </ul>

          <h3>d. Technical Information</h3>
          <ul>
            <li>IP address</li>
            <li>Browser type and device information</li>
            <li>Log files and platform activity data</li>
          </ul>
        </section>

        <section>
          <h2>2. How We Use Your Information</h2>
          <ul>
            <li>To process loan applications and evaluate creditworthiness</li>
            <li>To facilitate loan disbursals and manage EMI repayment schedules</li>
            <li>To verify identity and fulfill statutory KYC requirements</li>
            <li>To send automated EMI reminders, updates, and support responses</li>
            <li>To enhance risk management algorithms and platform security</li>
            <li>To comply with regulatory standards and legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>3. Data Sharing & Disclosure</h2>
          <p>We do not sell or rent your personal or financial information.</p>
          <p>Your information may be shared only:</p>
          <ul>
            <li>
              With authorized lending partners, credit bureaus, and payment collection gateways strictly for loan servicing
            </li>
            <li>When mandated by court orders, laws, or regulatory authorities</li>
            <li>
              To safeguard the rights, property, and security of {company.name} and its borrowers
            </li>
          </ul>
          <p>All third-party service providers are contractually bound to maintain strict confidentiality.</p>
        </section>

        <section>
          <h2>4. Data Security</h2>
          <p>
            We implement advanced technical, administrative, and physical
            security controls to safeguard borrower data, including:
          </p>
          <ul>
            <li>End-to-end SSL encryption for sensitive financial transactions</li>
            <li>Secured databases with role-based access restrictions</li>
            <li>Continuous security monitoring and audit logging</li>
          </ul>
          <p>
            While we apply industry-standard security protocols, no internet-based system can guarantee absolute protection.
          </p>
        </section>

        <section>
          <h2>5. Cookies & Tracking Technologies</h2>
          <p>
            {company.name} may utilize cookies and tracking technologies to:
          </p>
          <ul>
            <li>Maintain user session safety during loan application filling</li>
            <li>Analyze borrower navigation workflows and platform performance</li>
            <li>Optimize system efficiency</li>
          </ul>
          <p>
            You can disable cookies in browser preferences, though certain loan management functionalities may be restricted.
          </p>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>We retain borrower data only for as long as necessary:</p>
          <ul>
            <li>To service active loan contracts and settlement workflows</li>
            <li>To satisfy applicable statutory, financial audit, and legal retention mandates</li>
          </ul>
          <p>
            Upon expiry of retention mandates, data is securely wiped or completely anonymized.
          </p>
        </section>

        <section>
          <h2>7. Borrower Rights</h2>
          <p>You reserve the right to:</p>
          <ul>
            <li>Access your loan profile and credit documents</li>
            <li>Request updates or corrections to incomplete profile records</li>
            <li>Request profile deletion (subject to ongoing loan clearance obligations)</li>
            <li>Opt-out of non-essential operational communications</li>
          </ul>
          <p>
            Requests can be addressed to: <br />
            📧 <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links</h2>
          <p>
            Our application may include links to third-party payment gateways or partner portals.{" "}
            {company.name} is not responsible for the privacy policies or content of external sites.
          </p>
        </section>

        <section>
          <h2>9. Minors Policy</h2>
          <p>
            Our credit and loan products are restricted strictly to individuals aged 18 and older.
            We do not knowingly process data belonging to minors.
          </p>
        </section>

        <section>
          <h2>10. Changes to This Policy</h2>
          <p>
            {company.name} reserves the right to revise this Privacy Policy as regulatory or product updates demand.
            Revisions are effective immediately upon posting to this page.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            For any queries or grievances regarding this Privacy Policy or your data security, reach us at:
          </p>
          <p>
            📧 <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </p>
        </section>

        <section>
          <h2>Disclaimer</h2>
          <p>
            {company.name} operates as a micro-finance and loan management software provider. All credit allocations, disbursals, and terms remain subject to borrower eligibility and lender partner policies.
          </p>
        </section>
      </div>

      <Footerlanding />
    </div>
  );
};

export default PrivacyPolicy;